import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { AppConfigService as ConfigService, TokenService } from '@cudirect/shared-ui-library';
import { XApiKeyService } from '@shared/service/x-api-key.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { StorageService } from '@workflow/service/storage/storage.service';
import { UserSessionService } from '@shared/service/user-session.service';
import { WorkflowPageNavigationStateService } from '@workflow/service/workflow-page-navigation-state/workflow-page-navigation-state.service';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';
import { StatefulService } from '@app/service/stateful.service';
import { CUDLConnect } from '@workflow/ecp-workflow/dynamic-app/component/building-block/app-prefill/app-prefill.type';
import { OIDLoginWidgetExportData } from '@cudirect/ea-login';
import { ConsumerLogin } from 'src/user/model/consumer-login';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { LoanType } from '@shared/enum';
import { LenderService } from '@shared/service/lender/lender.service';

interface HandleReceivedDataForLoginModeResponse {
  phoneNumber?: string;
  email?: string;
}

interface GetLoginDataProps {
  email: string;
  phoneNumber: string;
  accessToken: string;
  refreshToken: string;
  ssoLogin?: boolean;
  loginMode: 'login' | 'heavyReg' | 'verify';
  isOlaFlow: boolean;
}

interface GetLoginDataResponse {
  oldSession: string;
  user: ConsumerLogin;
  prevSession: string;
  comingFromHeavyReg: string;
}

interface PrefillAuthenticationResult {
  success: boolean;
  userInfo?: UserInfo;
  error?: HttpErrorResponse;
}

/**
 * Service responsible for handling prefill authentication flows for both OLA and OAO.
 * Encapsulates the complex authentication logic including token exchange, user setup,
 * and session management.
 */
@Injectable({
  providedIn: 'root'
})
export class PrefillAuthenticationService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Inject all required services
  private readonly tokenService = inject(TokenService);
  private readonly configService = inject(ConfigService);
  private readonly xApiKeyService = inject(XApiKeyService);
  private readonly userService = inject(UserService);
  private readonly storageService = inject(StorageService);
  private readonly userSession = inject(UserSessionService);
  private readonly pageNavigationStateService = inject(WorkflowPageNavigationStateService);
  private readonly l360Service = inject(L360Service);
  private readonly googleTrackingService = inject(GoogleTrackingService);
  private readonly statefulService = inject(StatefulService);
  private readonly logger = inject(SawDust);
  private readonly lenderService = inject(LenderService);

  /**
   * Executes the complete prefill authentication flow for OLA or OAO.
   * 
   * This method orchestrates the entire authentication process including:
   * - Token validation and exchange
   * - MFA method extraction
   * - User setup and session management
   * - Backend initialization
   * - Google Analytics event tracking
   *
   * @param cudlConnectResponse - The CUDL Connect response containing prefill data, tokens, and applicant information
   * @param isOla - Whether this is an OLA flow (true) or OAO flow (false)
   * @param context - Optional context string for logging and analytics (e.g., 'Homebanking Redirect', 'OAO Membership'). Defaults to 'Prefill Authentication'
   * @returns Promise resolving to authentication result with success status and optional userInfo. Returns `{ success: false }` on failure
   * @throws May throw HttpErrorResponse if authentication fails
   */
  public async executePrefillAuthenticationFlow(
    cudlConnectResponse: CUDLConnect,
    isOla: boolean,
    context = 'Prefill Authentication'
  ): Promise<PrefillAuthenticationResult> {
    const flowType = isOla ? 'OLA' : 'OAO';
    const logContext = { service: 'PrefillAuthenticationService', flowType, context };

    try {
      // Validate tokens
      if (!cudlConnectResponse?.internalJwtToken?.access_token || !cudlConnectResponse?.internalJwtToken?.refresh_token) {
        this.publishGoogleAnalyticsEvent(flowType, 'prefill-error', context);
        this.logger.warn('Prefill data missing required tokens', {
          ...logContext,
          hasAccessToken: !!cudlConnectResponse?.internalJwtToken?.access_token,
          hasRefreshToken: !!cudlConnectResponse?.internalJwtToken?.refresh_token
        });
        return { success: false };
      }

      // Retrieve loginType from StatefulValue
      const loginType = (this.statefulService.wrap('api:loginType').value as string) || isOla ? 'ola' : 'oao';

      // Extract MFA verification method
      const { isValid: isValidMfa, method: mfaVerifyMethod, phone: phoneValue, email: emailValue } = this.extractMfaVerifyMethod(cudlConnectResponse);

      if (!isValidMfa) {
        this.publishGoogleAnalyticsEvent(flowType, 'prefill-error', context);
        this.logger.warn('Prefill data missing MFA verification method', logContext);
        throw new Error('Missing or invalid MFA method');
      }

      const { access_token: accessToken, refresh_token: refreshToken } = cudlConnectResponse.internalJwtToken;

      // Hide user dropdown until user has passed personal info
      this.userService.isExistingUser = cudlConnectResponse.isExistingUser;

      // Update token service
      this.tokenService.updateToken({
        access_token: accessToken,
        refresh_token: refreshToken,
        expires_in: 0,
        id_token: null,
        token_type: null
      });

      const loginMode = 'verify';

      // Execute authentication flow
      await this.setupTokenInitialize();

      const verifyFlowData = await this.setUpVerifyFlow({ refreshToken, accessToken });
      const receivedData = await this.receivedDataHandler(verifyFlowData);
      const handledData = await this.handleReceivedDataForLoginMode(receivedData);

      const loginData = await this.getLoginData({
        loginMode,
        email: mfaVerifyMethod === 'Email_Code' ? handledData.email : emailValue,
        phoneNumber: mfaVerifyMethod === 'Phone' ? handledData.phoneNumber : phoneValue,
        accessToken,
        refreshToken,
        ssoLogin: false,
        isOlaFlow: isOla
      });

      const { oldSession, user, comingFromHeavyReg } = loginData;

      // Initialize backends if needed
      if (oldSession && !comingFromHeavyReg && this.storageService.isAllBackendInitialized()) {
        await this.getLoginDataContinued({ oldSession });
      } else {
        await this.storageService.initAllBackends();
        await this.getLoginDataContinued({ oldSession });
      }

      // Publish success event
      this.publishGoogleAnalyticsEvent(flowType, 'prefill-success', context);

      // Set user information
      const { userInfo } = await this.setUser({ user, oldSession, loginType, mfaVerifyMethod });

      // Handle OAO-specific logic
      if (this.userService.user?.loginType && this.userService.user.loginType.toLowerCase() === 'oao') {
        await this.afterLogin({ userInfo, loginMode });
        await this.setOAOMember({ userInfo });
      }

      this.logger.info('Prefill authentication flow completed successfully', logContext);
      return { success: true, userInfo };
    } catch (error) {
      this.publishGoogleAnalyticsEvent(flowType, 'prefill-error', context);
      this.logger.error('Error during prefill authentication flow', error as Error, logContext);
      return { success: false, error: error as HttpErrorResponse };
    }
  }

  /**
   * Initializes the token service with configuration settings and sets up caching for user session tokens.
   * 
   * This method configures the token service with:
   * - OID API base URL from app configuration
   * - API headers with x-api-key
   * - OID configuration name
   * - Cache configuration using session storage or specified storage type
   *
   * @returns Promise that resolves when token service is fully initialized and configured
   * @private
   */
  private async setupTokenInitialize(): Promise<void> {
    return this.tokenService
      .initialize(
        this.configService?.settings?.oid.apiBaseURL,
        this.xApiKeyService.createApiHeader(),
        this.lenderService.lenderDataInfo?.oidConfigName ? this.lenderService.lenderDataInfo.oidConfigName : this.configService.settings?.oid.oidConfigName,
        true
      )
      .then(() => {
        this.tokenService.configureCache(this.configService?.settings?.arcDXTokenStorageType || 'sessionStorage', 'ecp-user-session');
      });
  }

  /**
   * Sets up the verify flow for the login process by creating a mock login widget response.
   * 
   * Constructs an OIDLoginWidgetExportData object with the provided tokens to simulate
   * a successful authentication response from the login widget.
   *
   * @param params - Object containing authentication tokens
   * @param params.accessToken - The JWT access token for API authentication
   * @param params.refreshToken - The refresh token for obtaining new access tokens
   * @returns Promise resolving to a formatted OIDLoginWidgetExportData object with 'Authenticated' status
   * @private
   */
  private async setUpVerifyFlow({
    accessToken,
    refreshToken
  }: {
    accessToken: string;
    refreshToken: string;
  }): Promise<OIDLoginWidgetExportData> {
    const data = {
      loginRedirect: false,
      provider: '',
      statusMsg: 'Authenticated',
      token: {
        access_token: accessToken,
        refresh_token: refreshToken,
        expires_in: 0,
        id_token: null,
        token_type: null
      }
    } as OIDLoginWidgetExportData;

    return Promise.resolve(data);
  }

  /**
   * Handles the received data from the login widget.
   * 
   * Acts as a pass-through handler that can be extended for additional
   * processing of login widget data in the future.
   *
   * @param data - The login widget export data containing authentication information
   * @returns Promise resolving to the same data object passed in
   * @private
   */
  private async receivedDataHandler(data: OIDLoginWidgetExportData): Promise<OIDLoginWidgetExportData> {
    return Promise.resolve(data);
  }

  /**
   * Processes the received data from the login widget and extracts user contact information.
   * 
   * Decodes the JWT access token to extract email and phone number from the token claims.
   * Handles both string tokens and token objects with access_token property.
   *
   * @param data - The login widget export data containing the access token
   * @returns Promise resolving to an object containing the user's phone number and email
   * @throws Error if token format is invalid
   * @private
   */
  private async handleReceivedDataForLoginMode(data: OIDLoginWidgetExportData): Promise<HandleReceivedDataForLoginModeResponse> {
    let accessToken: string;

    if (typeof data.token === 'string') {
      accessToken = data.token;
    } else if (data.token && 'access_token' in data.token) {
      accessToken = data.token.access_token;
    } else {
      throw new Error('Invalid token format');
    }

    const decodedToken = this.tokenService.decodeToken(accessToken);
    const { email, phonenumber } = decodedToken;

    return Promise.resolve({
      phoneNumber: phonenumber,
      email
    });
  }

  /**
   * Processes login data and prepares user information for authentication.
   * 
   * Creates a ConsumerLogin object with user credentials, retrieves previous session data,
   * and determines if the user is coming from heavy registration flow.
   *
   * @param params - Object containing login data properties
   * @param params.email - User's email address (defaults to empty string)
   * @param params.phoneNumber - User's phone number (defaults to empty string)
   * @param params.accessToken - JWT access token (defaults to empty string)
   * @param params.refreshToken - Refresh token (defaults to empty string)
   * @param params.ssoLogin - Whether user logged in via SSO (defaults to false)
   * @param params.loginMode - The login mode: 'login', 'heavyReg', or 'verify'
   * @param params.isOlaFlow - Whether this is an OLA flow (true) or OAO flow (false)
   * @returns Promise resolving to login data including old session, user object, previous session, and heavy reg flag
   * @private
   */
  private async getLoginData({
    email = '',
    phoneNumber = '',
    accessToken = '',
    refreshToken = '',
    ssoLogin = false,
    loginMode,
    isOlaFlow
  }: GetLoginDataProps): Promise<GetLoginDataResponse> {
    const user = {} as ConsumerLogin;
    user.email = email;
    user.accessToken = accessToken;
    user.refreshToken = refreshToken;
    user.loginWithSSO = ssoLogin;
    user.cellPhone = phoneNumber;

    const oldSession = this.statefulService.wrap(`${isOlaFlow ? 'ss:auth-ola' : 'ss:authorization'}`).valueString;
    let prevSession = '';
    if (loginMode === 'verify') {
      prevSession = oldSession ?? null;
    }
    this.statefulService.wrap('ss:loginType').value = false;
    const comingFromHeavyReg = this.isBrowser ? sessionStorage.getItem('arcDXHeavyReg') : null;

    return Promise.resolve({
      oldSession,
      user,
      prevSession,
      comingFromHeavyReg
    });
  }

  /**
   * Continues the login process after initial login data has been retrieved.
   * 
   * Handles cleanup of heavy registration flag from session storage,
   * restores user session if hooking to current bundle, and resets the bundle hook flag.
   *
   * @param params - Object containing session information
   * @param params.oldSession - The previous session token to restore
   * @returns Promise that resolves when continuation logic is complete
   * @private
   */
  private async getLoginDataContinued({ oldSession }: { oldSession: string }): Promise<void> {
    if (this.isBrowser) {
      sessionStorage.removeItem('arcDXHeavyReg');
    }
    if (this.userSession.canHookCurrentBundle && oldSession) {
      this.userService.user = new UserInfo();
      this.userService.user.token = oldSession;
    }
    this.userSession.canHookCurrentBundle = false;
    return Promise.resolve();
  }

  /**
   * Sets up the user information based on the provided login data.
   * 
   * Creates and populates a UserInfo object with authentication data,
   * assigns it to the user service, sets the flow type (OLA/OAO),
   * starts the user session timer, and stores the session token.
   *
   * @param params - Object containing user setup information
   * @param params.user - The ConsumerLogin object with user credentials
   * @param params.oldSession - The session token to restore
   * @param params.mfaVerifyMethod - The MFA verification method: 'Email_Code' or 'Phone'
   * @param params.loginType - Optional login type string (ola/oao)
   * @returns Promise resolving to an object containing the populated UserInfo
   * @private
   */
  private async setUser({
    user,
    oldSession,
    loginType,
    mfaVerifyMethod
  }: {
    user: ConsumerLogin;
    oldSession: string;
    mfaVerifyMethod: 'Email_Code' | 'Phone';
    loginType?: string;
  }): Promise<{ userInfo: UserInfo }> {
    const userInfo = new UserInfo();
    userInfo.email = user.email;
    userInfo.cellPhone = user.cellPhone;
    userInfo.isCellPhoneUser = !!user.cellPhone;
    userInfo.mfaVerifyMethod = mfaVerifyMethod;
    userInfo.token = oldSession;
    userInfo.token_time = new Date();
    userInfo.confetti = [];
    userInfo.registeredWithSSO = user.loginWithSSO;
    userInfo.loginType = loginType;

    this.userService.user = userInfo;
    if (loginType?.toLowerCase() === LoanType.OLA.toLowerCase()) {
      this.userService.flowType = LoanType.OLA;
    } else if (loginType?.toLowerCase() === LoanType.OAO.toLowerCase()) {
      this.userService.flowType = LoanType.OAO;
    }

    this.userSession.startTimer();
    this.statefulService.wrap('ss:authorization').value = oldSession;

    return Promise.resolve({ userInfo });
  }

  /**
   * Performs post-login operations based on the user information and login mode.
   * 
   * Executes OAO-specific initialization by setting lender settings via L360 service
   * if the user's login type is OAO.
   *
   * @param params - Object containing post-login information
   * @param params.userInfo - The UserInfo object with user details
   * @param params.loginMode - The login mode: 'login', 'heavyReg', or 'verify'
   * @returns Promise that resolves when post-login operations are complete
   * @private
   */
  private async afterLogin({ userInfo, loginMode }: { userInfo: UserInfo; loginMode: 'login' | 'heavyReg' | 'verify' }): Promise<void> {
    if (this.userService.user?.loginType && this.userService.user.loginType.toLowerCase() === 'oao') {
      this.l360Service.setOAOLenderSettings();
    }
    return Promise.resolve();
  }

  /**
   * Sets up the OAO member information and initializes related services.
   * 
   * Configures the page navigation state service for OAO workflow by setting
   * the storage prefix to 'oao' and retrieving the saved page state.
   *
   * @param params - Object containing user information
   * @param params.userInfo - The UserInfo object with user details
   * @returns Promise that resolves when OAO member setup is complete
   * @private
   */
  private async setOAOMember({ userInfo }: { userInfo: UserInfo }): Promise<void> {
    if (userInfo) {
      this.pageNavigationStateService.setStoragePrefix('oao');
      this.pageNavigationStateService.retrievePageState();
    }
    return Promise.resolve();
  }

  /**
   * Extracts the Multi-Factor Authentication (MFA) verification method from the provided CUDLConnect response.
   * 
   * Analyzes the applicant data to determine the appropriate MFA method.
   * Priority order: Mobile phone (if available), then email.
   * Extracts both phone and email when available for fallback purposes.
   *
   * @param res - The CUDLConnect response containing applicant information
   * @returns Object containing MFA method details:
   *   - isValid: Whether a valid MFA method was found
   *   - method: The MFA method type ('Email_Code' or 'Phone')
   *   - value: The primary contact value for the selected method
   *   - phone: The user's mobile phone number (if available)
   *   - email: The user's email address (if available)
   * @private
   */
  private extractMfaVerifyMethod(res: CUDLConnect): {
    isValid: boolean;
    method?: 'Email_Code' | 'Phone';
    value?: string;
    phone?: string;
    email?: string;
  } {
    if (!res?.cudlAccountLink?.applicant || !Array.isArray(res.cudlAccountLink.applicant)) {
      return { isValid: false };
    }

    const applicants = res.cudlAccountLink.applicant;
    if (!applicants.length) {
      return { isValid: false };
    }

    const retval = {
      isValid: false
    };

    const applicant = applicants[0];
    if (applicant.phoneNumber && Array.isArray(applicant.phoneNumber)) {
      const mobilePhone = applicant.phoneNumber.find((phone) => phone.phoneNumberType === 'Mobile');
      if (mobilePhone?.number) {
        Object.assign(retval, {
          isValid: true,
          method: 'Phone',
          value: mobilePhone.number,
          phone: mobilePhone.number
        });
      }
    }

    if (applicant.emailAddress.length > 0) {
      Object.assign(retval, {
        email: applicant.emailAddress[0]
      });

      if (!retval.isValid) {
        Object.assign(retval, {
          isValid: true,
          method: 'Email_Code',
          value: applicant.emailAddress[0]
        });
      }
    }

    return retval;
  }

  /**
   * Publishes a Google Analytics event for prefill authentication tracking.
   * 
   * Constructs a GA action name in the format:
   * - 'arcdx.loans.{eventSuffix}' for OLA flows
   * - 'arcdx.accounts.{eventSuffix}' for OAO flows
   * 
   * Includes page context and application type as event parameters.
   *
   * @param flowType - The flow type: 'OLA' or 'OAO'
   * @param eventSuffix - The event suffix (e.g., 'prefill-success', 'prefill-error')
   * @param page - The page or context where the event occurred
   * @returns void
   * @private
   */
  private publishGoogleAnalyticsEvent(flowType: string, eventSuffix: string, page: string): void {
    let gaActionName = 'arcdx.';
    if (flowType === 'OLA') {
      gaActionName += 'loans.';
    } else {
      gaActionName += 'accounts.';
    }
    gaActionName += eventSuffix;
    
    this.googleTrackingService.publishEvent(gaActionName, {
      Page: page,
      ApplicationType: flowType
    });
  }
}
